var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import LoadingSpinner from "@/components/app/LoadingSpinner.vue";
import Button from "@/components/ui/button/Button.vue";
import Panel from "@/components/ui/panel/Panel.vue";
import { ExportButton } from "@/core/export";
import { FieldRepository } from "@/core/fields";
import { buildObjective } from "@/core/objectives";
import PeriodSelector from "@/core/period/PeriodSelector.vue";
import { buildTableFields, computeObjectiveValues, useReportDataset, } from "@/core/report";
import DatasetFilter from "@/core/report/components/DatasetFilter.vue";
import DatasetTable from "@/core/report/components/DatasetTable.vue";
import PaginatorControls from "@/core/report/components/PaginatorControls.vue";
import PaginatorInfo from "@/core/report/components/PaginatorInfo.vue";
import PaginatorItemsPerPage from "@/core/report/components/PaginatorItemsPerPage.vue";
import SortContextMenu from "@/core/report/components/SortContextMenu.vue";
import TableZoomDropdown from "@/core/report/components/TableZoomDropdown.vue";
import { useTableZoom } from "@/core/report/use-table-zoom";
import { generateTableBody } from "@/core/table";
import { fetchObjectiveFields } from "@/views/objectives-report/api/fields";
import { ClipboardCheckIcon, SettingsIcon, UploadIcon } from "lucide-vue-next";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { fetchPendingChangesForTable } from "./api/changes";
import { fetchReportData } from "./api/data";
import { fetchAssignedObjectives, parseObjectiveConfig } from "./api/objectives";
import { DEFAULT_SORTS, getFilterFields, PRESET_SORTS, SORTABLE_FIELDS, TABLE_FIELDS } from "./config";
import BulkEditDialog from "./features/bulk-edit/BulkEditDialod.vue";
import ChangeReviewDialog from "./features/change-review/ChangeReviewDialog.vue";
import { useChangeReview } from "./features/change-review/use-change-review";
import ObjectiveAssignmentDialog from "./features/objective-assignment/ObjectiveAssignmentDialog.vue";
import { useObjectiveAssignment } from "./features/objective-assignment/use-objective-assignment";
import { currentUserHasRole, currentUserHasAnyRole } from "@/core/users/roles";
import { currentUser } from "@/core/users/session";
import { asNumber } from "@/lib/number-utils";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ObjectivesReportView',
    setup(__props, { expose: __expose }) {
        __expose();
        const { tableZoom } = useTableZoom();
        const fields = new FieldRepository();
        // Period management - initialize with current month/year
        const selectedPeriod = ref(null);
        // Data state
        const isLoading = ref(false);
        const error = ref(null);
        const rawData = ref([]);
        const tableData = ref([]);
        const objectives = ref([]);
        const fieldsLoaded = ref(false);
        const headerKeys = ref([]);
        const tab = ref("");
        const isNorthReadonlyUser = computed(() => currentUser().area_code === "N" && !currentUser().region_code);
        const isSouthDefaultObjectivesUser = computed(() => { var _a; return ["S100", "S200"].includes((_a = currentUser().region_code) !== null && _a !== void 0 ? _a : ""); });
        const canBulkEdit = computed(() => {
            var _a;
            return !isNorthReadonlyUser.value &&
                !["N100", "N300", "E100", "E200", "C200"].includes((_a = currentUser().region_code) !== null && _a !== void 0 ? _a : "") &&
                currentUserHasAnyRole(["Gestor de Distribuidor", "Distribuidor"]);
        });
        const canReviewChanges = computed(() => {
            var _a;
            return !isNorthReadonlyUser.value &&
                !["N100", "N300", "E100", "E200", "C200"].includes((_a = currentUser().region_code) !== null && _a !== void 0 ? _a : "") &&
                currentUserHasRole("Validador");
        });
        const canManageObjectives = computed(() => !isNorthReadonlyUser.value && !isSouthDefaultObjectivesUser.value);
        // Pending changes state
        const pendingChanges = ref([]);
        // Objective assignment
        const objectiveAssignment = useObjectiveAssignment();
        const assignmentDialogRef = ref();
        const bulkEditDialogRef = ref();
        // Change review
        const changeReview = useChangeReview();
        const reviewDialogRef = ref();
        // Computed: Pending change requests count
        const pendingChangeRequestsCount = computed(() => changeReview.changeRequests.value.length);
        // Computed: Get assigned objective IDs
        // NOTE: objectives.value for S300 users is built from dataResponse.objectiveConfigs which uses raw
        // integer IDs, but allObjectives uses base64-encoded IDs from ObjetivoResource — id-match is unsafe.
        // Name-match is safe: both sides source name from the same DB column via ObjetivoResource (or the
        // data-v2 raw path), so a name migration updates both sets atomically.
        const assignedObjectiveIds = computed(() => {
            const assignedNames = new Set(objectives.value.map((obj) => obj.name));
            return objectiveAssignment.allObjectives.value
                .filter((obj) => assignedNames.has(obj.name))
                .map((obj) => obj.id);
        });
        // Computed: Process data with objectives
        // tableData is now a ref updated in loadData
        // Computed: Build table fields (static + objectives)
        // Defensa extra: ocultar siempre las columnas de crecimiento de Volumen si llegaran por cualquier motivo
        const removeHiddenFields = (columns, hiddenKeys) => {
            return columns.flatMap((field) => {
                var _a;
                if (field.type !== "group") {
                    return hiddenKeys.has(field.key) ? [] : [field];
                }
                const children = ((_a = field.children) !== null && _a !== void 0 ? _a : []).filter((child) => !hiddenKeys.has(child.key));
                if (children.length === 0) {
                    return [];
                }
                return [Object.assign(Object.assign({}, field), { children })];
            });
        };
        const tableFields = computed(() => {
            // Dependency on fieldsLoaded to trigger recomputation when fields are loaded
            if (!fieldsLoaded.value)
                return [];
            const hiddenKeys = new Set([
                "columnas_seleccionadas_center_volumen_castilla.crecimiento_aant",
                "columnas_seleccionadas_center_volumen_castilla.crec_acum",
            ]);
            // Use dynamic headerKeys from API, fallback to TABLE_FIELDS if empty
            const staticFields = headerKeys.value.length > 0 ? headerKeys.value : TABLE_FIELDS;
            // Build fields and ensure static fields have proper width configuration
            const isNorthUser = currentUser().area_code === "N";
            const objectivesForDisplay = isNorthUser
                ? objectives.value.filter(obj => obj.type !== 'canon')
                : objectives.value;
            const builtFields = removeHiddenFields(buildTableFields(fields, staticFields, objectivesForDisplay), hiddenKeys)
                .map((field) => {
                // Add width to static fields if they don't have groups (to prevent misalignment)
                if (field.type !== 'group' && !field.width) {
                    // Apply default widths for known fields
                    const widthMap = {
                        'id': '80px',
                        'region_de_ventas': '120px',
                        'delegacion_zona_de_ventas': '220px',
                        'sala': '100px',
                        'distribuidor': '300px',
                        'ano_natural': '100px',
                        'mes_natural': '100px',
                        'updated_at': '180px',
                    };
                    return Object.assign(Object.assign({}, field), { width: widthMap[field.key] || undefined });
                }
                return field;
            });
            const canonObj = objectives.value.find(obj => obj.type === 'canon');
            if (isNorthUser && canonObj) {
                builtFields.push({
                    key: canonObj.id,
                    label: canonObj.name,
                    type: 'group',
                    color: canonObj.color,
                    children: [
                        { key: `${canonObj.id}.red_total`, label: 'Red Total', type: 'percent', align: 'right' },
                        { key: `${canonObj.id}.periodo_actual`, label: 'Periodo actual', type: 'float', align: 'right' },
                        { key: `${canonObj.id}.periodo_anterior`, label: 'Periodo anterior', type: 'float', align: 'right' },
                        { key: `${canonObj.id}.porcentaje_crecimiento`, label: '% crecimiento', type: 'percent', align: 'right' },
                        { key: `${canonObj.id}.comision`, label: 'Comisión', type: 'string', align: 'right' },
                        { key: `${canonObj.id}.importe_total`, label: 'Importe total', type: 'float', align: 'right' },
                    ],
                });
            }
            return builtFields;
        });
        // Initialize dataset
        const dataset = useReportDataset({
            tableData: () => tableData.value,
            tableFields: () => tableFields.value,
            filterFields: getFilterFields(),
            sortableFields: SORTABLE_FIELDS,
            defaultSorts: DEFAULT_SORTS,
            itemsPerPage: 10,
        });
        const updateTableData = () => __awaiter(this, void 0, void 0, function* () {
            if (rawData.value.length > 0) {
                const computed = yield computeObjectiveValues(rawData.value, objectives.value);
                if (currentUser().area_code === "N" && objectives.value.length > 0) {
                    const kpi1Obj = objectives.value.find(obj => obj.type === 'clientes_north');
                    const kpi2Obj = objectives.value.find(obj => obj.type === 'volumen_north');
                    const kpi3Obj = objectives.value.find(obj => obj.type === 'clientes_north_fuze');
                    const canonObj = objectives.value.find(obj => obj.type === 'canon');
                    if (canonObj) {
                        tableData.value = computed.map(row => {
                            var _a, _b, _c, _d, _e, _f;
                            const kpi1Data = kpi1Obj ? row[kpi1Obj.id] : null;
                            const kpi2Data = kpi2Obj ? row[kpi2Obj.id] : null;
                            const kpi3Data = kpi3Obj ? row[kpi3Obj.id] : null;
                            const canonData = row[canonObj.id];
                            const kpi1Red = asNumber((_a = kpi1Data === null || kpi1Data === void 0 ? void 0 : kpi1Data.porcentaje_red) !== null && _a !== void 0 ? _a : 0);
                            const kpi2Red = asNumber((_b = kpi2Data === null || kpi2Data === void 0 ? void 0 : kpi2Data.porcentaje_red) !== null && _b !== void 0 ? _b : 0);
                            const kpi3Red = kpi3Data != null ? asNumber((_c = kpi3Data.porcentaje_red) !== null && _c !== void 0 ? _c : 0) : null;
                            const red_total = kpi3Red != null
                                ? kpi1Red * 0.5 + kpi2Red * 0.5 * (14 / 15) + kpi3Red * 0.5 * (1 / 15)
                                : kpi1Red * 0.5 + kpi2Red * 0.5;
                            const canonPeriodoActual = asNumber((_d = canonData === null || canonData === void 0 ? void 0 : canonData.periodo_actual) !== null && _d !== void 0 ? _d : 0);
                            const canonPeriodoAnterior = asNumber((_e = canonData === null || canonData === void 0 ? void 0 : canonData.periodo_anterior) !== null && _e !== void 0 ? _e : 0);
                            const comision = asNumber((_f = row.comision) !== null && _f !== void 0 ? _f : 0);
                            return Object.assign(Object.assign({}, row), { [canonObj.id]: Object.assign(Object.assign({}, row[canonObj.id]), { red_total, porcentaje_crecimiento: canonPeriodoAnterior !== 0 ? (canonPeriodoActual / canonPeriodoAnterior) - 1 : 0, comision, importe_total: red_total * canonPeriodoActual * comision }) });
                        });
                        return;
                    }
                }
                tableData.value = computed;
            }
            else {
                tableData.value = [];
            }
        });
        const serializeFields = (columns) => {
            return columns.map((field) => ({
                key: field.key,
                label: field.label,
                type: field.type,
                align: field.align,
                width: field.width,
                color: field.color,
                children: field.children ? serializeFields(field.children) : undefined,
            }));
        };
        const publishCaptureData = () => __awaiter(this, void 0, void 0, function* () {
            if (!window.__OBJECTIVES_REPORT_CAPTURE__)
                return;
            yield nextTick();
            const columns = tableFields.value;
            const rows = dataset.sortedData.value;
            window.__OBJECTIVES_REPORT_EXPORT__ = {
                ready: true,
                tab: tab.value,
                period: selectedPeriod.value,
                rowCount: rows.length,
                columns: serializeFields(columns),
                rows,
                formattedRows: generateTableBody(rows, columns),
            };
        });
        const publishCaptureLoading = () => {
            if (!window.__OBJECTIVES_REPORT_CAPTURE__)
                return;
            window.__OBJECTIVES_REPORT_EXPORT__ = {
                ready: false,
                tab: tab.value,
                period: selectedPeriod.value,
            };
        };
        const loadFields = () => __awaiter(this, void 0, void 0, function* () {
            if (fieldsLoaded.value)
                return;
            try {
                const response = yield fetchObjectiveFields();
                fields.register(response);
                fieldsLoaded.value = true;
            }
            catch (err) {
                console.warn("Failed to load field definitions:", err);
            }
        });
        // Load data
        const loadData = () => __awaiter(this, void 0, void 0, function* () {
            try {
                isLoading.value = true;
                error.value = null;
                publishCaptureLoading();
                const [dataResponse, objectivesResponse] = yield Promise.all([
                    fetchReportData(selectedPeriod.value, tab.value),
                    fetchAssignedObjectives(tab.value),
                    loadFields(),
                ]);
                rawData.value = dataResponse.data;
                headerKeys.value = dataResponse.headerKeys;
                if (isSouthDefaultObjectivesUser.value || currentUser().region_code === "S300") {
                    const assignedObjectiveNames = new Set(objectivesResponse.map((objective) => objective.name));
                    objectives.value = dataResponse.objectiveConfigs
                        .filter((objectiveConfig) => assignedObjectiveNames.size === 0 || assignedObjectiveNames.has(objectiveConfig.name))
                        .map((objectiveConfig) => {
                        var _a;
                        // Match by the stable name (above); display the period-dependent title in the table.
                        const parsed = parseObjectiveConfig(objectiveConfig, tab.value);
                        return buildObjective(Object.assign(Object.assign({}, parsed), { name: (_a = objectiveConfig.display_name) !== null && _a !== void 0 ? _a : objectiveConfig.name }));
                    });
                }
                else {
                    const presentObjectiveNames = new Set(dataResponse.objectiveNames);
                    objectives.value = presentObjectiveNames.size > 0
                        ? objectivesResponse.filter((objective) => presentObjectiveNames.has(objective.name))
                        : objectivesResponse;
                }
                yield updateTableData();
                yield publishCaptureData();
                if (canBulkEdit.value) {
                    // Load pending changes after data is loaded
                    try {
                        pendingChanges.value = yield fetchPendingChangesForTable({
                            period: selectedPeriod.value,
                            dataRows: dataResponse.data,
                        });
                    }
                    catch (err) {
                        console.warn("Failed to load pending changes:", err);
                        pendingChanges.value = [];
                    }
                }
                else {
                    pendingChanges.value = [];
                }
            }
            catch (err) {
                rawData.value = [];
                headerKeys.value = [];
                objectives.value = [];
                error.value = "Error al cargar los datos";
                if (window.__OBJECTIVES_REPORT_CAPTURE__) {
                    window.__OBJECTIVES_REPORT_EXPORT__ = {
                        ready: true,
                        tab: tab.value,
                        period: selectedPeriod.value,
                        error: error.value,
                    };
                }
                console.error("Error loading report data:", err);
            }
            finally {
                isLoading.value = false;
            }
        });
        watch(objectives, updateTableData);
        // Load all available objectives and data on mount
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            try {
                yield objectiveAssignment.loadAllObjectives(tab.value, selectedPeriod.value);
                yield loadData();
                if (canReviewChanges.value && selectedPeriod.value) {
                    yield changeReview.loadChangeRequests(selectedPeriod.value);
                }
            }
            catch (error) {
                console.error("Error loading objectives:", error);
            }
        }));
        // Handle manage objectives button click
        const handleManageObjectives = () => __awaiter(this, void 0, void 0, function* () {
            if (!assignmentDialogRef.value)
                return;
            try {
                const result = yield assignmentDialogRef.value.open();
                if (result.action === "accept" && result.data) {
                    yield objectiveAssignment.syncAssignments(result.data, tab.value);
                    // Reload data after syncing assignments
                    yield loadData();
                }
            }
            catch (error) {
                console.error("Error managing objectives:", error);
                alert("Error al gestionar los objetivos");
            }
        });
        // Handle bulk edit button click
        const handleBulkEdit = () => __awaiter(this, void 0, void 0, function* () {
            if (!bulkEditDialogRef.value)
                return;
            try {
                const result = yield bulkEditDialogRef.value.open();
                // If bulk edit was successful, reload the data and pending changes
                if (result.action === "accept" && result.data === true) {
                    yield Promise.all([
                        loadData(), // This will reload both data and pending changes
                        selectedPeriod.value && canReviewChanges.value ? changeReview.loadChangeRequests(selectedPeriod.value) : Promise.resolve(),
                    ]);
                }
            }
            catch (error) {
                console.error("Error during bulk edit:", error);
                alert("Error al realizar la edición masiva");
            }
        });
        // Handle review changes button click
        const handleReviewChanges = () => __awaiter(this, void 0, void 0, function* () {
            if (!reviewDialogRef.value)
                return;
            try {
                const result = yield reviewDialogRef.value.open();
                // If a change was approved/rejected, reload the data and pending changes
                if (result.action === "accept" && result.data === true) {
                    yield Promise.all([
                        loadData(), // This will reload both data and pending changes
                        selectedPeriod.value && canReviewChanges.value ? changeReview.loadChangeRequests(selectedPeriod.value) : Promise.resolve(),
                    ]);
                }
            }
            catch (error) {
                console.error("Error during change review:", error);
                alert("Error al revisar los cambios");
            }
        });
        // Auto-reload data when period changes
        watch(() => { var _a; return (_a = selectedPeriod.value) === null || _a === void 0 ? void 0 : _a.key; }, (newKey) => {
            if (newKey && selectedPeriod.value) {
                // Reload available objectives so dynamic titles match the selected period
                objectiveAssignment.loadAllObjectives(tab.value, selectedPeriod.value);
                loadData();
                // Also load change requests for the selected period
                if (canReviewChanges.value) {
                    changeReview.loadChangeRequests(selectedPeriod.value);
                }
            }
        });
        watch(() => tab.value, (newVal) => __awaiter(this, void 0, void 0, function* () {
            yield objectiveAssignment.loadAllObjectives(tab.value, selectedPeriod.value);
            loadData();
        }));
        const __returned__ = { tableZoom, fields, selectedPeriod, isLoading, error, rawData, tableData, objectives, fieldsLoaded, headerKeys, tab, isNorthReadonlyUser, isSouthDefaultObjectivesUser, canBulkEdit, canReviewChanges, canManageObjectives, pendingChanges, objectiveAssignment, assignmentDialogRef, bulkEditDialogRef, changeReview, reviewDialogRef, pendingChangeRequestsCount, assignedObjectiveIds, removeHiddenFields, tableFields, dataset, updateTableData, serializeFields, publishCaptureData, publishCaptureLoading, loadFields, loadData, handleManageObjectives, handleBulkEdit, handleReviewChanges, LoadingSpinner, Button, Panel, get ExportButton() { return ExportButton; }, PeriodSelector, DatasetFilter, DatasetTable, PaginatorControls, PaginatorInfo, PaginatorItemsPerPage, SortContextMenu, TableZoomDropdown, get ClipboardCheckIcon() { return ClipboardCheckIcon; }, get SettingsIcon() { return SettingsIcon; }, get UploadIcon() { return UploadIcon; }, get PRESET_SORTS() { return PRESET_SORTS; }, BulkEditDialog, ChangeReviewDialog, ObjectiveAssignmentDialog, get currentUser() { return currentUser; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
